package co.maytes.standupbot.dashboard.features.settings.sections

import co.maytes.standupbot.dashboard.components.motion.StateSwap
import co.maytes.standupbot.dashboard.features.settings.phase
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.Badge
import co.maytes.standupbot.dashboard.components.widgets.BadgeTone
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.Select
import co.maytes.standupbot.dashboard.components.widgets.SelectOption
import co.maytes.standupbot.dashboard.components.widgets.Switch
import co.maytes.standupbot.dashboard.components.widgets.Textarea
import co.maytes.standupbot.dashboard.features.integrations.LoadState
import co.maytes.standupbot.dashboard.features.settings.LeaveKeywordPreview
import co.maytes.standupbot.dashboard.features.settings.LocalSettingsNavigate
import co.maytes.standupbot.dashboard.features.settings.Plain
import co.maytes.standupbot.dashboard.features.settings.SettingsBars
import co.maytes.standupbot.dashboard.features.settings.SettingsError
import co.maytes.standupbot.dashboard.features.settings.SettingsField
import co.maytes.standupbot.dashboard.features.settings.SettingsGroup
import co.maytes.standupbot.dashboard.features.settings.SettingsPage
import co.maytes.standupbot.dashboard.features.settings.SettingsPanel
import co.maytes.standupbot.dashboard.features.settings.SettingsPreview
import co.maytes.standupbot.dashboard.features.settings.SettingsRowItem
import co.maytes.standupbot.dashboard.features.settings.SettingsSaveBar
import co.maytes.standupbot.dashboard.features.settings.SettingsSectionRow
import co.maytes.standupbot.dashboard.features.settings.SettingsSubtitle
import co.maytes.standupbot.dashboard.features.settings.Strong
import co.maytes.standupbot.dashboard.features.settings.TimezonePreview
import co.maytes.standupbot.dashboard.features.settings.rememberSettingsStore
import co.maytes.standupbot.dashboard.features.settings.rememberWorkspacePatcher
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.auth.PlanDto
import co.maytes.standupbot.shared.dto.workspace.DEFAULT_LEAVE_KEYWORDS
import co.maytes.standupbot.shared.dto.workspace.UpdateWorkspaceRequestDto
import co.maytes.standupbot.shared.dto.workspace.WorkspaceDto
import co.maytes.standupbot.shared.validation.LeaveKeywordsValidator
import co.maytes.standupbot.shared.validation.ValidationResult
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.todayIn

private val TIMEZONES = listOf(
    "UTC",
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "Europe/London",
    "Europe/Berlin",
    "Europe/Madrid",
    "Europe/Amsterdam",
    "Asia/Tokyo",
    "Asia/Singapore",
    "Asia/Kolkata",
    "Australia/Sydney",
)

@Composable
fun GeneralSection() {
    val auth = rememberAuthStore()
    val store = rememberSettingsStore()
    val scope = rememberCoroutineScope()
    val workspaceId = auth.currentUser?.workspace?.id

    LaunchedEffect(workspaceId) {
        if (!workspaceId.isNullOrBlank()) store.refreshWorkspace(workspaceId)
    }

    val ws = store.workspace
    SettingsPage(
        group = SettingsGroup.Workspace,
        title = "General",
        subtitle = ws?.let {
            {
                SettingsSubtitle {
                    Strong(it.slackTeamName)
                    Plain(" · ${it.plan.displayLabel()} plan · Slack team ${it.slackTeamId}")
                }
            }
        },
    ) {
        StateSwap(state = store.workspaceState.phase()) {
            when (val state = store.workspaceState) {
                LoadState.Idle, LoadState.Loading -> GeneralLoading()
                is LoadState.Error -> SettingsSectionRow(
                    anchor = "workspace",
                    title = "Workspace",
                    description = "Name and defaults for everything in this workspace.",
                ) {
                    SettingsPanel {
                        SettingsError(what = "the workspace settings") {
                            scope.launch { workspaceId?.let { store.refreshWorkspace(it) } }
                        }
                    }
                }
                LoadState.Ready -> if (ws != null) GeneralLoaded(ws) else Unit
            }
        }
    }
}

@Composable
private fun GeneralLoading() {
    listOf(
        Triple("workspace", "Workspace", "Name and defaults for everything in this workspace."),
        Triple("sign-in", "Sign-in", "Who can open the dashboard with Slack."),
        Triple("time-off", "Time off", "People who are away are left out of the standup."),
    ).forEach { (anchor, title, description) ->
        SettingsSectionRow(anchor = anchor, title = title, description = description) {
            SettingsPanel { SettingsBars(rows = 3) }
        }
    }
}

@Composable
private fun GeneralLoaded(ws: WorkspaceDto) {
    val ctx = rememberPageContext()
    val navigate = LocalSettingsNavigate.current

    WorkspacePanel(ws, onPlan = { navigate("billing") })
    SignInPanel(ws)
    TimeOffPanel(ws)
    SettingsSectionRow(
        anchor = "onboarding",
        title = "Onboarding",
        description = "Walk back through the Slack, Linear and first-standup flow. Useful for new admins or after a " +
            "connection has been reset.",
    ) {
        SettingsPanel {
            SettingsRowItem(
                title = "Replay the onboarding tour",
                first = true,
                trailing = {
                    Button(
                        label = "Launch tour",
                        variant = ButtonVariant.Secondary,
                        size = ButtonSize.Sm,
                        onClick = { ctx.router.navigateTo("/app/onboarding/welcome") },
                    )
                },
            )
        }
    }
}

@Composable
private fun WorkspacePanel(ws: WorkspaceDto, onPlan: () -> Unit) {
    val patcher = rememberWorkspacePatcher()
    var tzDraft by remember(ws.id, ws.defaultTimezone) { mutableStateOf(ws.defaultTimezone) }
    val dirty = tzDraft != ws.defaultTimezone
    val today = Clock.System.todayIn(TimeZone.UTC)

    SettingsSectionRow(
        anchor = "workspace",
        title = "Workspace",
        description = "Name and defaults for everything in this workspace.",
        aside = listOf("Created" to ws.createdAt.toString().take(DATE_CHARS), "Slack team" to ws.slackTeamId),
    ) {
        SettingsPanel {
            SettingsField(label = "Workspace name", help = "Pulled from Slack. Change it on your Slack admin page.") {
                SpanText(
                    text = ws.slackTeamName,
                    modifier = Modifier.fontSize(Typography.label.size).fontWeight(400).color(Palette.textPrimary),
                )
            }
            SettingsField(
                label = "Default timezone",
                help = "Used for standups that haven't set their own.",
            ) {
                Select(
                    value = tzDraft,
                    options = buildList {
                        TIMEZONES.forEach { add(SelectOption(it, it)) }
                        if (tzDraft !in TIMEZONES && tzDraft.isNotBlank()) add(SelectOption(tzDraft, tzDraft))
                    },
                    onValueChange = { tzDraft = it },
                    modifier = Modifier.fillMaxWidth().styleModifier { property("max-width", MAX_FIELD_WIDTH) },
                )
            }
            TimezonePreview.describe(tzDraft, today)?.let { text ->
                SettingsPreview(label = "What this does") { Plain(text) }
            }
            SettingsField(label = "Plan") {
                SettingsRowItem(
                    title = ws.plan.displayLabel(),
                    first = true,
                    description = planDescription(ws.plan),
                    titleTrailing = { Badge(label = "Current", tone = BadgeTone.Accent) },
                    trailing = {
                        Button(
                            label = "Plan and billing",
                            variant = ButtonVariant.Ghost,
                            size = ButtonSize.Sm,
                            onClick = onPlan,
                        )
                    },
                )
            }
            SettingsSaveBar(
                open = dirty,
                saving = patcher.saving,
                hint = patcher.error ?: "Unsaved changes",
                onSave = {
                    patcher.save(UpdateWorkspaceRequestDto(defaultTimezone = tzDraft), "Default timezone saved")
                },
                onReset = { tzDraft = ws.defaultTimezone },
            )
        }
    }
}

@Composable
private fun SignInPanel(ws: WorkspaceDto) {
    val patcher = rememberWorkspacePatcher()
    var draft by remember(ws.id, ws.autoProvisionMembers) { mutableStateOf(ws.autoProvisionMembers) }
    val dirty = draft != ws.autoProvisionMembers

    SettingsSectionRow(
        anchor = "sign-in",
        title = "Sign-in",
        description = "Who can open the dashboard with Slack.",
    ) {
        SettingsPanel {
            SettingsRowItem(
                title = if (draft) "Anyone in your Slack workspace" else "Invited people only",
                first = true,
                description = if (draft) {
                    "Any member of your Slack workspace can sign in to the dashboard and is created as a member."
                } else {
                    "Unknown Slack users are rejected until an owner pre-creates them."
                },
                trailing = {
                    Switch(
                        checked = draft,
                        onCheckedChange = { draft = it },
                        ariaLabel = "Auto-provision members on first sign-in",
                    )
                },
            )
            SettingsSaveBar(
                open = dirty,
                saving = patcher.saving,
                hint = patcher.error ?: "Unsaved changes",
                onSave = {
                    patcher.save(UpdateWorkspaceRequestDto(autoProvisionMembers = draft), "Sign-in access saved")
                },
                onReset = { draft = ws.autoProvisionMembers },
            )
        }
    }
}

@Composable
private fun TimeOffPanel(ws: WorkspaceDto) {
    val patcher = rememberWorkspacePatcher()
    var detect by remember(ws.id, ws.leaveDetectSlackStatus) { mutableStateOf(ws.leaveDetectSlackStatus) }
    var keywords by remember(ws.id, ws.leaveKeywords) { mutableStateOf(ws.leaveKeywords.ifBlank { DEFAULT_LEAVE_KEYWORDS }) }
    val keywordsError = (LeaveKeywordsValidator.validate(keywords) as? ValidationResult.Error)?.message
    val dirty = detect != ws.leaveDetectSlackStatus || LeaveKeywordsValidator.normalize(keywords) != ws.leaveKeywords

    SettingsSectionRow(
        anchor = "time-off",
        title = "Time off",
        description = "People who are away are left out of the standup and never get a reminder.",
    ) {
        SettingsPanel {
            SettingsRowItem(
                title = "Read leave from Slack status",
                first = true,
                description = "Uses each person's public Slack status text and emoji to spot leave.",
                trailing = {
                    Switch(
                        checked = detect,
                        onCheckedChange = { detect = it },
                        ariaLabel = "Detect leave from Slack status",
                    )
                },
            )
            if (detect) {
                SettingsField(
                    label = "Leave keywords",
                    help = "Comma separated, up to ${LeaveKeywordsValidator.MAX_TERMS}. A status also counts as leave " +
                        "when it carries a palm tree, plane or sick emoji.",
                ) {
                    Textarea(
                        value = keywords,
                        onValueChange = { keywords = it },
                        rows = 3,
                        minHeightPx = 72,
                        error = keywordsError,
                        ariaLabel = "Leave keywords",
                    )
                }
                SettingsPreview(label = "Sample status “${LeaveKeywordPreview.SAMPLE_STATUS}”") {
                    val hit = LeaveKeywordPreview.firstMatch(keywords)
                    if (hit != null) {
                        Plain("Matches ")
                        Strong("“$hit”")
                        Plain(". That person would be skipped and not reminded.")
                    } else {
                        Plain("No keyword matches. That person would still get a reminder.")
                    }
                }
            }
            SettingsSaveBar(
                open = dirty,
                saving = patcher.saving,
                canSave = keywordsError == null,
                hint = patcher.error ?: keywordsError ?: "Unsaved changes",
                onSave = {
                    patcher.save(
                        UpdateWorkspaceRequestDto(leaveDetectSlackStatus = detect, leaveKeywords = keywords),
                        "Time off saved",
                    )
                },
                onReset = {
                    detect = ws.leaveDetectSlackStatus
                    keywords = ws.leaveKeywords.ifBlank { DEFAULT_LEAVE_KEYWORDS }
                },
            )
        }
    }
}

private const val DATE_CHARS = 10
private const val MAX_FIELD_WIDTH = "26rem"

private fun PlanDto.displayLabel(): String = when (this) {
    PlanDto.FREE -> "Free"
    PlanDto.PRO -> "Pro"
    PlanDto.BUSINESS -> "Business"
}

private fun planDescription(plan: PlanDto): String = when (plan) {
    PlanDto.FREE -> "Up to 1 standup, 5 members, 30-day history."
    PlanDto.PRO -> "Unlimited standups and members, 1-year history."
    PlanDto.BUSINESS -> "Pro plus SSO, 3-year history, audit CSV, priority support."
}
